import { useEffect } from "react";
import useSWR from "swr";
import { v4 as uuid } from "uuid";
import { fetcher } from "lib/actions/fetcher";
import { useUploader } from "../useUploader";
import { UploadSessionEntityType } from "lib/contexts/GlobalContexts/EntityUploadSessionsContext";
import { EndpointKey } from "lib/contexts/GlobalContexts/UploadContext";
import { UploadStatus } from "lib/types/UploadStatus";
import { useEntityUploadSessions } from "../useEntityUploadSessions";
import { endpointsAssets } from "lib/actions/endpoints/endpointsAssets";

export const useAssetUploads = (projectId: number) => {
  const { mutate: mutateIngress } = useSWR(
    projectId && endpointsAssets.projectIngresses(projectId),
    fetcher,
  );
  const { uploads, addFile, startFile, errorFile, changeUploadId } =
    useUploader();
  const { sessions, addSessions, changeSessionId } = useEntityUploadSessions();

  const projectUploads =
    projectId &&
    sessions?.Assets?.[projectId] &&
    uploads?.filter((u) => sessions?.Assets?.[projectId]?.includes(u.id));

  const uploadFile = async (
    file: File,
    registerCallback: () => Promise<{ uploadId: number }>,
  ) => {
    if (!projectId) throw new Error("projectId required.");

    const tempId = uuid();
    addFile(EndpointKey.ProjectFiles, tempId, file);
    addSessions({
      entityType: UploadSessionEntityType.Assets,
      entityId: projectId,
      uploadIds: [tempId],
    });

    let uploadId: string;
    try {
      const r = await registerCallback();
      uploadId = String(r.uploadId);
    } catch (e) {
      console.error("Could not register upload for file", file.name, e);
      errorFile(tempId);
      return;
    }

    if (!uploadId) {
      console.error("Register did not return a valid uploadId. Got", uploadId);
      errorFile(tempId);
    } else {
      changeSessionId(tempId, uploadId);
      changeUploadId(tempId, uploadId);
      startFile(uploadId);
      mutateIngress();
    }
  };

  //mutate project files when uploads complete
  const completeUploadsCount =
    projectUploads?.filter((u) => u.status === UploadStatus.complete).length ??
    0;
  useEffect(() => {
    if (completeUploadsCount > 0) mutateIngress();
  }, [completeUploadsCount]);

  const hasError = !projectId;

  return {
    uploads: projectUploads,
    hasError,
    uploadFile,
  };
};
